import React from 'react'
import styled from '@emotion/styled'
import { Image, Spin } from 'antd'

import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  hasDisplayableRelationship,
  relationshipLabel,
} from '@sportninja/common/features/profile/family/utils'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { isCanlan } from '@sportninja/common/utils/customer-name'

import {
  heading1,
  heading6,
  paragraphLarge,
  paragraphXSmall,
  weightBold,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import type { FamilyInvite } from '@sportninja/common/features/profile/family/types'

const PLAYER_PLACEHOLDER = '/images/generic_placeholders/placeholder_player.png'

const condensedTitleFont = isCanlan
  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important'
  : 'BarlowCondensed, Arial, Helvetica, sans-serif !important'

export interface FamilyInvitationProps {
  invite: FamilyInvite
  accepted: boolean
  loading: { page: boolean; accept: boolean }
  self: { id?: string } | null
  onConfirm: (userId?: string) => void
  inviteeEmail?: string | null
  inviteeName?: string | null
}

/**
 * Card for a family (user_to_player) invitation. Shows the player's image and
 * relationship-aware copy, with two states: pre-accept and accepted.
 */
const FamilyInvitation: React.FC<FamilyInvitationProps> = ({
  invite,
  accepted,
  loading,
  self,
  onConfirm,
  inviteeEmail,
  inviteeName,
}) => {
  const playerName =
    invite?.destination?.name_full || invite?.destination?.name || ''
  const hasRelation = hasDisplayableRelationship(invite?.relationship_type)
  const relation = relationshipLabel(invite?.relationship_type).toLowerCase()
  const image = getImageThumbnailId(invite) || undefined

  const goHome = () => {
    window.location.href = ROUTES.HOME
  }
  const goToProfile = () => {
    window.location.href = ROUTES.PROFILE.ROOT
  }

  return (
    <Page>
      <Card>
        <PlayerImage
          width={80}
          height={80}
          src={image}
          fallback={PLAYER_PLACEHOLDER}
          preview={false}
        />

        <Title>
          {accepted
            ? capitalizeWords('Ready To Go!')
            : capitalizeWords('JOIN THE FAMILY')}
        </Title>

        <Body>
          {!accepted ? (
            <InviteeRow>
              {inviteeName ? <InviteeName>{inviteeName}</InviteeName> : null}
              {inviteeEmail ? (
                <InviteeEmail>({inviteeEmail})</InviteeEmail>
              ) : null}
            </InviteeRow>
          ) : null}

          {!accepted ? (
            <Subtitle>
              You&apos;ve been invited to join{' '}
              <Highlight>{playerName}&apos;s</Highlight>
              {hasRelation ? (
                <>
                  {' '}
                  family as their <Highlight>{relation}</Highlight>
                </>
              ) : null}
            </Subtitle>
          ) : (
            <Subtitle>
              You are now connected to <Highlight>{playerName}</Highlight>
              {hasRelation ? (
                <>
                  {' '}
                  as their <Highlight>{relation}</Highlight>
                </>
              ) : null}
            </Subtitle>
          )}
        </Body>

        <Divider />

        <Actions>
          <OutlineButton type='button' onClick={goHome}>
            <OutlineButtonText>
              {accepted ? 'GO HOME' : 'NOT NOW'}
            </OutlineButtonText>
          </OutlineButton>
          <PrimaryButton
            type='button'
            disabled={loading.accept}
            $busy={loading.accept}
            onClick={() => {
              if (accepted) {
                goToProfile()
              } else {
                onConfirm(self?.id)
              }
            }}
          >
            {loading.accept ? (
              <Spin style={{ color: colors.WHITE }} />
            ) : (
              <PrimaryButtonText>
                {accepted ? 'GO TO THE PROFILE' : 'ACCEPT'}
              </PrimaryButtonText>
            )}
          </PrimaryButton>
        </Actions>
      </Card>
    </Page>
  )
}

export default FamilyInvitation

// ---- styled ----
const Page = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
`

const Card = styled.div`
  display: flex;
  width: 600px;
  padding: 40px 24px;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${colors.SECONDARY_300};
  background: ${colors.SECONDARY_400};
  @media (max-width: 768px) {
    max-width: 100vw;
    min-width: 0;
  }
`

const PlayerImage = styled(Image)`
  border-radius: 8px;
  object-fit: cover;
`

const Title = styled.p`
  color: ${colors.WHITE};
  text-align: center;
  text-transform: uppercase;
  ${heading1}
  font-family: ${condensedTitleFont};
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

const InviteeRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`

const InviteeName = styled.p`
  color: ${colors.PRIMARY};
  text-align: center;
  ${paragraphLarge}
  ${weightBold}
`

const InviteeEmail = styled.p`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  text-align: center;
`

const Subtitle = styled.p`
  ${paragraphLarge}
  ${weightRegular}
  color: ${colors.WHITE};
  text-align: center;
  align-self: center;
`

const Highlight = styled.span`
  ${paragraphLarge}
  ${weightBold}
  color: ${colors.PRIMARY};
`

const Divider = styled.div`
  background: ${colors.HEADER};
  height: 1px;
  width: 100%;
`

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const OutlineButton = styled.button`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 2px solid ${colors.WHITE};
  background: transparent;
  cursor: pointer;
  &,
  & * {
    cursor: pointer;
  }
`

const OutlineButtonText = styled.span`
  color: ${colors.WHITE};
  ${heading6}
  ${weightSemiBold}
  font-family: ${condensedTitleFont};
`

const PrimaryButton = styled.button<{ $busy: boolean }>`
  display: flex;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 8px;
  border: 2px solid ${colors.PRIMARY};
  background: ${colors.PRIMARY};
  cursor: pointer;
  &,
  & * {
    cursor: pointer;
  }
  ${({ $busy }) => ($busy ? 'opacity: 0.5;' : '')}
  &:disabled,
  &:disabled * {
    cursor: not-allowed;
  }
`

const PrimaryButtonText = styled.span`
  ${heading6}
  ${weightSemiBold}
  font-family: ${condensedTitleFont};
  color: ${colors.WHITE};
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
